/**
 * Diarization (speaker identification) module
 * Handles adding speaker diarization to episodes via batch jobs
 */

import { showNotification } from '../components/notifications.js';
import { showConfirmModal } from '../components/modal.js';

// Track active batch polling intervals
const activeBatchPolls = new Map();

/**
 * Check and add diarization for all episodes in a podcast
 */
export async function checkAndAddDiarization(podcastId, podcastTitle) {
    try {
        // First, check how many episodes need diarization
        const checkResponse = await fetch(`/api/podcasts/${podcastId}/check-missing-diarization`);
        const checkData = await checkResponse.json();

        if (checkData.missing_diarization === 0) {
            showNotification('All episodes already have diarization!', 'success');
            return;
        }

        // Show confirmation dialog
        const confirmed = await showConfirmModal({
            title: 'Add Speaker Diarization',
            message: `<div style="margin-bottom: 1rem;">
                Found <strong style="color: var(--warning);">${checkData.missing_diarization}</strong> out of
                <strong>${checkData.total_with_transcription}</strong> episodes missing speaker diarization.
            </div>
            <div style="color: var(--text-secondary);">
                This will submit them for speaker identification processing.
            </div>`,
            confirmText: 'Start Processing',
            cancelText: 'Cancel',
            icon: '🎤'
        });

        if (!confirmed) return;

        // Create batch job
        const response = await fetch(`/api/podcasts/${podcastId}/batch-diarize`, {
            method: 'POST'
        });

        const batchData = await response.json();

        if (!batchData.batch_id) {
            showNotification(batchData.message || 'No episodes need diarization', 'info');
            return;
        }

        showNotification(`Started diarization batch for ${batchData.total} episodes`, 'success');

        // Show progress modal and start polling
        showBatchProgressModal(batchData.batch_id, podcastTitle, 'diarization');

    } catch (error) {
        console.error('Error in checkAndAddDiarization:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * Show batch progress modal with polling
 */
function showBatchProgressModal(batchId, podcastTitle, batchType) {
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = `
        <div class="modal-content" style="max-width: 600px;">
            <h2>🎤 ${batchType === 'diarization' ? 'Diarization' : 'Processing'} - ${podcastTitle}</h2>
            <div class="progress-container">
                <div class="progress-bar" id="batch-progress-bar" style="width: 0%"></div>
                <div class="progress-text" id="batch-progress-text">0%</div>
            </div>

            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin: 1.5rem 0;">
                <div class="stat-card">
                    <div class="stat-value" id="batch-total-count">0</div>
                    <div class="stat-label">Total</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value" id="batch-pending-count">0</div>
                    <div class="stat-label">Pending</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value" id="batch-completed-count">0</div>
                    <div class="stat-label">Completed</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value" id="batch-failed-count">0</div>
                    <div class="stat-label">Failed</div>
                </div>
            </div>

            <div id="batch-status-text" style="text-align: center; color: var(--text-secondary); margin-bottom: 1rem;">
                Processing...
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.5rem;">
                <button id="batch-cancel-btn" class="btn btn-secondary">Cancel</button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    const progressBar = modal.querySelector('#batch-progress-bar');
    const progressText = modal.querySelector('#batch-progress-text');
    const totalCount = modal.querySelector('#batch-total-count');
    const pendingCount = modal.querySelector('#batch-pending-count');
    const completedCount = modal.querySelector('#batch-completed-count');
    const failedCount = modal.querySelector('#batch-failed-count');
    const statusText = modal.querySelector('#batch-status-text');
    const cancelBtn = modal.querySelector('#batch-cancel-btn');

    // Poll for batch status
    const pollInterval = setInterval(async () => {
        try {
            const response = await fetch(`/api/batches/${batchId}`);
            const status = await response.json();

            totalCount.textContent = status.total;
            pendingCount.textContent = status.pending + (status.submitted || 0);
            completedCount.textContent = status.completed;
            failedCount.textContent = status.failed;

            const processed = status.completed + status.failed;
            const percentage = status.total > 0 ? Math.round((processed / status.total) * 100) : 0;
            progressBar.style.width = percentage + '%';
            progressText.textContent = percentage + '%';

            if (status.status === 'completed' || status.status === 'cancelled') {
                clearInterval(pollInterval);
                activeBatchPolls.delete(batchId);

                progressBar.style.width = '100%';
                progressText.textContent = '100%';

                statusText.style.color = 'var(--success)';
                statusText.textContent = `✓ ${status.status === 'cancelled' ? 'Cancelled' : 'Complete'}! ${status.completed} successful, ${status.failed} failed`;

                cancelBtn.textContent = 'Close';
                cancelBtn.onclick = () => {
                    modal.remove();
                    if (window.loadPodcasts) {
                        window.loadPodcasts();
                    }
                };

                showNotification(
                    `Batch ${status.status}! ${status.completed} successful, ${status.failed} failed`,
                    status.status === 'completed' ? 'success' : 'info'
                );
            } else {
                statusText.textContent = `Processing... ${processed}/${status.total} items`;
            }

        } catch (error) {
            console.error('Error polling batch status:', error);
        }
    }, 3000); // Poll every 3 seconds

    activeBatchPolls.set(batchId, pollInterval);

    cancelBtn.onclick = async () => {
        clearInterval(pollInterval);
        activeBatchPolls.delete(batchId);

        // Cancel the batch on server
        try {
            await fetch(`/api/batches/${batchId}`, { method: 'DELETE' });
            showNotification('Batch cancelled', 'info');
        } catch (error) {
            console.error('Error cancelling batch:', error);
        }

        modal.remove();
    };
}

/**
 * Redo diarization for individual episode using batch API
 */
export async function redoDiarization(episodeId) {
    try {
        const confirmed = await showConfirmModal({
            title: 'Reprocess Diarization',
            message: 'This will reprocess speaker diarization for this episode.',
            confirmText: 'Reprocess',
            cancelText: 'Cancel',
            icon: '🎤'
        });
        if (!confirmed) return;

        showNotification('Submitting episode for diarization...', 'info');

        // Create a single-episode batch job
        const response = await fetch(`/api/episodes/${episodeId}/batch-diarize`, {
            method: 'POST'
        });

        const data = await response.json();

        if (!response.ok) {
            showNotification(`Error: ${data.detail || 'Failed to submit'}`, 'error');
            return;
        }

        if (!data.batch_id) {
            showNotification(data.message || 'Episode already has diarization', 'info');
            return;
        }

        showNotification('Diarization started. Processing in background...', 'success');

        // Poll batch status
        const batchId = data.batch_id;
        const pollInterval = setInterval(async () => {
            try {
                const statusResponse = await fetch(`/api/batches/${batchId}`);
                const status = await statusResponse.json();

                if (status.status === 'completed') {
                    clearInterval(pollInterval);
                    activeBatchPolls.delete(batchId);

                    if (status.completed > 0) {
                        showNotification('Diarization completed successfully!', 'success');
                    } else {
                        showNotification('Diarization failed. Check logs for details.', 'error');
                    }

                    // Refresh episode details
                    setTimeout(() => {
                        if (window.viewEpisodeDetails) {
                            window.viewEpisodeDetails(episodeId);
                        }
                    }, 500);

                } else if (status.status === 'cancelled') {
                    clearInterval(pollInterval);
                    activeBatchPolls.delete(batchId);
                    showNotification('Diarization cancelled', 'info');
                }

            } catch (error) {
                console.error('Error polling batch status:', error);
            }
        }, 3000); // Poll every 3 seconds

        activeBatchPolls.set(batchId, pollInterval);

    } catch (error) {
        console.error('Error in redoDiarization:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * View episodes with missing diarization
 */
export async function viewMissingDiarization(podcastId, podcastTitle, page = 0) {
    try {
        showNotification('Loading episodes with missing diarization...', 'info');

        const response = await fetch(`/api/podcasts/${podcastId}/check-missing-diarization`);

        if (!response.ok) {
            showNotification(`Error loading episodes: ${response.status}`, 'error');
            console.error('API error:', response.status, response.statusText);
            return;
        }

        const data = await response.json();
        const allEpisodes = data.episodes || [];
        const container = document.getElementById('podcasts');

        console.log('Loaded', allEpisodes.length, 'episodes missing diarization');

        // Get current podcast page for back button
        const currentPodcastPage = window.currentPodcastPage || 0;

        if (allEpisodes.length === 0) {
            container.innerHTML = `
                <button class="btn btn-secondary" onclick="loadPodcasts(${currentPodcastPage})" style="margin-bottom: 1.5rem;">← Back to Podcasts</button>
                <h2 style="margin-bottom: 1.5rem;">${podcastTitle} - Missing Diarization</h2>
                <div style="padding: 2rem; text-align: center; background: var(--card-bg); border-radius: 8px;">
                    <p style="color: var(--success); font-size: 1.2rem; margin-bottom: 1rem;">✓ All episodes have diarization!</p>
                    <p style="color: var(--text-secondary);">No episodes are missing speaker diarization data.</p>
                </div>
            `;
            return;
        }

        // Paginate results
        const perPage = 50;
        const totalPages = Math.ceil(allEpisodes.length / perPage);
        const startIdx = page * perPage;
        const endIdx = startIdx + perPage;
        const episodes = allEpisodes.slice(startIdx, endIdx);

        container.innerHTML = `
            <button class="btn btn-secondary" onclick="loadPodcasts(${currentPodcastPage})" style="margin-bottom: 1.5rem;">← Back to Podcasts</button>
            <h2 style="margin-bottom: 1rem;">${podcastTitle} - Missing Diarization</h2>

            <div style="margin-bottom: 1.5rem; padding: 1rem; background: var(--warning); color: var(--bg); border-radius: 8px;">
                <strong>${allEpisodes.length} episode${allEpisodes.length !== 1 ? 's' : ''}</strong> with transcriptions but missing speaker diarization
                <div style="margin-top: 0.5rem; font-size: 0.9rem;">Showing ${startIdx + 1}-${Math.min(endIdx, allEpisodes.length)} of ${allEpisodes.length}</div>
            </div>

            ${totalPages > 1 ? `
                <div class="pagination" style="margin-bottom: 1.5rem; display: flex; justify-content: center; gap: 0.5rem; align-items: center;">
                    <button class="btn btn-small ${page === 0 ? 'btn-disabled' : ''}"
                            onclick="viewMissingDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}', ${page - 1})"
                            ${page === 0 ? 'disabled' : ''}>
                        ← Previous
                    </button>
                    <span style="margin: 0 1rem;">
                        Page ${page + 1} of ${totalPages}
                    </span>
                    <button class="btn btn-small ${page >= totalPages - 1 ? 'btn-disabled' : ''}"
                            onclick="viewMissingDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}', ${page + 1})"
                            ${page >= totalPages - 1 ? 'disabled' : ''}>
                        Next →
                    </button>
                </div>
            ` : ''}

            <div class="episodes-grid" id="missing-diar-grid">
            </div>

            ${totalPages > 1 ? `
                <div class="pagination" style="margin-top: 1.5rem; display: flex; justify-content: center; gap: 0.5rem; align-items: center;">
                    <button class="btn btn-small ${page === 0 ? 'btn-disabled' : ''}"
                            onclick="viewMissingDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}', ${page - 1})"
                            ${page === 0 ? 'disabled' : ''}>
                        ← Previous
                    </button>
                    <span style="margin: 0 1rem;">
                        Page ${page + 1} of ${totalPages}
                    </span>
                    <button class="btn btn-small ${page >= totalPages - 1 ? 'btn-disabled' : ''}"
                            onclick="viewMissingDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}', ${page + 1})"
                            ${page >= totalPages - 1 ? 'disabled' : ''}>
                        Next →
                    </button>
                </div>
            ` : ''}
        `;

        // Render episodes using DOM manipulation
        const gridContainer = document.getElementById('missing-diar-grid');
        episodes.forEach(episode => {
            const episodeDiv = document.createElement('div');
            episodeDiv.className = 'episode-item';
            episodeDiv.style.cssText = 'background: var(--card-bg); padding: 1rem; border-radius: 8px; border: 1px solid var(--border); margin-bottom: 1rem;';

            const flexContainer = document.createElement('div');
            flexContainer.style.cssText = 'display: flex; justify-content: space-between; align-items: start; gap: 1rem;';

            const infoDiv = document.createElement('div');
            infoDiv.style.cssText = 'flex: 1; cursor: pointer;';
            infoDiv.onclick = () => {
                if (window.viewEpisodeDetails) {
                    window.viewEpisodeDetails(episode.id);
                }
            };

            const titleDiv = document.createElement('div');
            titleDiv.className = 'episode-title';
            titleDiv.style.cssText = 'margin-bottom: 0.5rem;';
            titleDiv.textContent = episode.title;

            const badgesDiv = document.createElement('div');
            badgesDiv.style.cssText = 'display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem;';
            badgesDiv.innerHTML = `
                ${episode.has_local_file ?
                    '<span class="status-badge status-success">Downloaded</span>' :
                    '<span class="status-badge status-warning">Not Downloaded</span>'
                }
                <span class="status-badge status-accent">Has Transcription</span>
                <span class="status-badge status-error">Missing Diarization</span>
            `;

            if (episode.published_date) {
                const dateP = document.createElement('p');
                dateP.style.cssText = 'color: var(--text-secondary); font-size: 0.9rem; margin: 0;';
                dateP.textContent = `Published: ${new Date(episode.published_date).toLocaleDateString()}`;
                infoDiv.appendChild(titleDiv);
                infoDiv.appendChild(badgesDiv);
                infoDiv.appendChild(dateP);
            } else {
                infoDiv.appendChild(titleDiv);
                infoDiv.appendChild(badgesDiv);
            }

            const button = document.createElement('button');
            button.className = 'btn btn-small';
            if (episode.has_local_file) {
                button.style.cssText = 'background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%); white-space: nowrap;';
                button.textContent = '🎤 Add Diarization';
                button.title = 'Add speaker diarization to this episode';
                button.onclick = (e) => {
                    e.stopPropagation();
                    redoDiarization(episode.id);
                };
            } else {
                button.classList.add('btn-disabled');
                button.disabled = true;
                button.textContent = 'Download Required';
                button.title = 'Episode must be downloaded first';
            }

            flexContainer.appendChild(infoDiv);
            flexContainer.appendChild(button);
            episodeDiv.appendChild(flexContainer);
            gridContainer.appendChild(episodeDiv);
        });

        showNotification(`Found ${allEpisodes.length} episode${allEpisodes.length !== 1 ? 's' : ''} missing diarization`, 'success');

    } catch (error) {
        console.error('Error viewing missing diarization:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * Refetch diarization for all episodes in a podcast (force mode)
 * Uses batch API with force=true
 */
export async function refetchAllDiarization(podcastId, podcastTitle) {
    try {
        const confirmed = await showConfirmModal({
            title: 'Refetch All Diarisation',
            message: `This will re-process speaker diarization for all downloaded episodes in "${podcastTitle}" using the Senko backend.`,
            confirmText: 'Start Processing',
            cancelText: 'Cancel',
            icon: '🎤'
        });

        if (!confirmed) return;

        // Create batch job with force=true
        const response = await fetch(`/api/podcasts/${podcastId}/batch-diarize?force=true`, {
            method: 'POST'
        });

        const batchData = await response.json();

        if (!batchData.batch_id) {
            showNotification(batchData.message || 'No episodes to process', 'info');
            return;
        }

        showNotification(`Started diarization batch for ${batchData.total} episodes`, 'success');
        showBatchProgressModal(batchData.batch_id, podcastTitle, 'diarization');

    } catch (error) {
        console.error('Error in refetchAllDiarization:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * Refetch both transcriptions and diarization for all episodes
 */
export async function refetchBoth(podcastId, podcastTitle) {
    try {
        const confirmed = await showConfirmModal({
            title: 'Refetch Transcriptions & Diarisation',
            message: `This will re-fetch both transcriptions and speaker diarization for all episodes in "${podcastTitle}". Both will run in background.`,
            confirmText: 'Start Processing',
            cancelText: 'Cancel',
            icon: '🔄'
        });

        if (!confirmed) return;

        // Create transcription batch first
        const transcResponse = await fetch(`/api/podcasts/${podcastId}/batch-transcribe?force=true`, {
            method: 'POST'
        });
        const transcData = await transcResponse.json();

        if (transcData.batch_id) {
            showNotification(`Started transcription batch for ${transcData.total} episodes`, 'success');
        }

        // Create diarization batch
        const diarResponse = await fetch(`/api/podcasts/${podcastId}/batch-diarize?force=true`, {
            method: 'POST'
        });
        const diarData = await diarResponse.json();

        if (diarData.batch_id) {
            showNotification(`Started diarization batch for ${diarData.total} episodes`, 'success');
            showBatchProgressModal(diarData.batch_id, podcastTitle, 'diarization');
        } else if (!transcData.batch_id) {
            showNotification('No episodes need processing', 'info');
        }

    } catch (error) {
        console.error('Error in refetchBoth:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}
